import React, { useEffect, useState } from 'react';
import { InfoCircle } from "react-bootstrap-icons";
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { useError } from "../../contexts/ErrorContext";
import { RecordsAPI } from '../../services/api';
import { Execution, Task } from '../../types';

interface RecordExecutionProps {
    execution: Execution;
    onExecutionUpdated: (execution: Execution) => void;
}

const RecordExecution: React.FC<RecordExecutionProps> = ({ execution, onExecutionUpdated }) => {
    const [currentExecution, setCurrentExecution] = useState<Execution>(execution);
    const [loading, setLoading] = useState<boolean>(false);
    const [canceling, setCanceling] = useState<boolean>(false);
    const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
    const [selectedTaskError, setSelectedTaskError] = useState<string>('');
    const { setError } = useError();

    // Update local state when prop changes
    useEffect(() => {
        setCurrentExecution(execution);
    }, [execution]);

    // Function to get the status badge color
    const getStatusBadgeVariant = (status: string): string => {
        switch (status.toLowerCase()) {
            case 'completed':
                return 'success';
            case 'started':
                return 'primary';
            case 'error':
                return 'danger';
            case 'canceled':
                return 'secondary';
            default:
                return 'secondary';
        }
    };

    // Function to format date
    const formatDate = (dateString: string | null): string => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    const toSortableTime = (dateString: string | null | undefined): number => {
        if (!dateString) return Number.MAX_SAFE_INTEGER;
        const parsed = Date.parse(dateString);
        return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
    };

    // Function to refresh execution data
    const refreshExecution = async () => {
        try {
            setLoading(true);
            const response = await RecordsAPI.getExecutionDetails(currentExecution.id);
            // Hand the whole freshly-synced execution up rather than only signalling a
            // status change: the record header derives both status and sub_status from
            // it, and sub_status can move while status stays 'error'.
            setCurrentExecution(response.data);
            onExecutionUpdated(response.data);
        } catch (error) {
            console.error('Error refreshing execution:', error);
            setError(`Error refreshing execution: ${error}`);
        } finally {
            setLoading(false);
        }
    };

    // Handle canceling an execution
    const handleCancelExecution = async () => {
        try {
            setCanceling(true);
            const response = await RecordsAPI.cancelExecution(currentExecution.id);
            setCurrentExecution(response.data);
            onExecutionUpdated(response.data);
        } catch (error: unknown) {
            console.error('Error canceling execution:', error);
            const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || `Error canceling execution: ${error}`;
            setError(errorMessage);
        } finally {
            setCanceling(false);
        }
    };

    const parseCustomStatus = (value?: string | null): Record<string, unknown> => {
        if (!value) return {};
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
                return (parsed[0] || {}) as Record<string, unknown>;
            }
            return typeof parsed === "object" && parsed !== null ? parsed : {};
        } catch (e) {
            console.error(e);
            return { truncated: value };
        }
    };

    const getTaskErrorDetails = (task: Task): string | null => {
        if (task.detailed_error && task.detailed_error.trim()) {
            return task.detailed_error;
        }

        const parsed = parseCustomStatus(task.custom_status);
        const fallbackMessage = parsed.message;
        if (typeof fallbackMessage === "string" && fallbackMessage.trim()) {
            return fallbackMessage;
        }

        return null;
    };

    const formatCustomStatusForDisplay = (
        value: string | null | undefined,
        taskStatus: string
    ): Record<string, unknown> => {
        const parsed = parseCustomStatus(value);
        const scrubbed: Record<string, unknown> = { ...parsed };
        delete scrubbed.stdout;
        delete scrubbed.stderr;
        delete scrubbed.payload;
        delete scrubbed.note;
        delete scrubbed.nodeId;
        delete scrubbed.returncode;
        delete scrubbed.ok;
        delete scrubbed.step;

        const ok = parsed.ok === true && parsed.returncode === 0;
        if (ok) {
            const details = parsed.details && typeof parsed.details === "object"
                ? parsed.details
                : { message: "Task completed successfully" };
            return { status: "success", details };
        }

        if (Object.keys(scrubbed).length > 0) {
            return scrubbed;
        }

        const fallbackMessage =
            (typeof parsed.error === "string" && parsed.error) ||
            (typeof parsed.message === "string" && parsed.message) ||
            "No details available";
        return { status: taskStatus, details: { message: fallbackMessage } };
    };

    // Handle showing error details
    const handleShowErrorDetails = (errorText: string) => {
        setSelectedTaskError(errorText);
        setShowErrorModal(true);
    };

    // Handle copying error to clipboard
    const handleCopyError = async () => {
        try {
            await navigator.clipboard.writeText(selectedTaskError);
            // You can add a success message here if needed
        } catch (err) {
            console.error('Failed to copy error text:', err);
        }
    };

    const sortedTasks = (currentExecution.tasks || []).slice().sort((a: Task, b: Task) => {
        const aStart = toSortableTime(a.start_date);
        const bStart = toSortableTime(b.start_date);
        if (aStart !== bStart) {
            return aStart - bStart;
        }

        const aEnd = toSortableTime(a.end_date);
        const bEnd = toSortableTime(b.end_date);
        if (aEnd !== bEnd) {
            return aEnd - bEnd;
        }

        return a.name.localeCompare(b.name);
    });

    return (
        <>
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <div>
                        Execution Details #{currentExecution.id}
                        <Badge
                            bg={getStatusBadgeVariant(currentExecution.status)}
                            className="ms-2"
                        >
                            {currentExecution.status}
                        </Badge>
                    </div>
                    <div className="d-flex gap-2">
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={handleCancelExecution}
                            disabled={
                                canceling ||
                                loading ||
                                !['completed', 'started'].includes(currentExecution.status.toLowerCase())
                            }
                            title={
                                ['completed', 'started'].includes(currentExecution.status.toLowerCase())
                                    ? 'Cancel this execution'
                                    : 'Can only cancel executions with status "completed" or "started"'
                            }
                        >
                            {canceling ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                'Cancel'
                            )}
                        </Button>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={refreshExecution}
                            disabled={loading}
                        >
                            {loading ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                'Refresh'
                            )}
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    <div className="mb-3">
                        <strong>Started:</strong> {formatDate(currentExecution.start_date)}
                        <br />
                        <strong>Ended:</strong> {formatDate(currentExecution.end_date)}
                        {(currentExecution.user_email || currentExecution.user_id) && (
                            <>
                                <br />
                                <strong>Created by:</strong>{' '}
                                {currentExecution.user_email || currentExecution.user_id}
                            </>
                        )}
                    </div>

                    <h6>Tasks</h6>
                    {currentExecution.custom_data?.argo_status_sync?.state === 'pending' && (
                        <p className="text-muted">Some step statuses are still being synchronized.</p>
                    )}
                    {sortedTasks.length > 0 ? (
                        <div className="table-responsive">
                            <Table striped bordered hover size="sm" style={{ tableLayout: "fixed" }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: "24%" }}>Name</th>
                                        <th style={{ width: "10%" }}>Status</th>
                                        <th style={{ width: "36%" }}>Custom Status</th>
                                        <th style={{ width: "12%" }}>Started</th>
                                        <th style={{ width: "12%" }}>Ended</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedTasks.map((task: Task) => {
                                        const statusData = formatCustomStatusForDisplay(task.custom_status, task.status);
                                        const taskErrorDetails = task.status.toLowerCase() === 'error'
                                            ? getTaskErrorDetails(task)
                                            : null;
                                        return (
                                            <tr key={task.id}>
                                                <td>{task.name}</td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <Badge bg={getStatusBadgeVariant(task.status)}>
                                                            {task.status}
                                                        </Badge>
                                                        {task.status.toLowerCase() === 'error' && taskErrorDetails && (
                                                            <Button
                                                                variant="outline-info"
                                                                size="sm"
                                                                onClick={() => handleShowErrorDetails(taskErrorDetails)}
                                                                title="View detailed error"
                                                            >
                                                                <InfoCircle />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>

                                                <td style={{ wordBreak: "break-word" }}>
                                                    <pre style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
                                                        {JSON.stringify(
                                                            statusData,
                                                            null,
                                                            2
                                                        )}
                                                    </pre>
                                                </td>
                                                <td>{formatDate(task.start_date)}</td>
                                                <td>{formatDate(task.end_date)}</td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <p className="text-muted">No tasks found for this execution.</p>
                    )}
                </Card.Body>
            </Card>

            {/* Error Details Modal */}
            <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Task Error Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <strong>Error Details:</strong>
                    </div>
                    <pre
                        className="bg-light p-3 border rounded"
                        style={{
                            maxHeight: '400px',
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                        }}
                    >
                        {selectedTaskError}
                    </pre>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowErrorModal(false)}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleCopyError}>
                        Copy Error
                    </Button>
                </Modal.Footer>
            </Modal>

        </>
    );
};

export default RecordExecution;
